import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _path from 'ramda/src/path';
import Connections from '../../API/Connections';
import Mixpanel from '../../API/Mixpanel';
import SendMessage from './SendMessage';
import { withMessage } from './Message';
import resx from '../../utilities/resx';
import GeneralUtility from '../../utilities/generalUtility';

import { RESX_GLOBAL, MIXPANEL_RELATIONSHIP, MIXPANEL_CONTENT, MIXPANEL_LAND } from '../../Constants';

const ConnectButton = props => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sendMessage, setSendMessage] = useState(false);
    const [connection, setConnection] = useState({
        ImFollowing: false,
        FollowingMe: false,
        Connected: false,
        UserId: props.userId
    });
    const [connections, setConnections] = useState([]);
    const [totalConnected, setTotalConnected] = useState(0);
    const [canConnect, setCanConnect] = useState(true);
    const isMe = props.userId === _path(['RESAAS', 'User2', 'UserId'], window);
    const imProfessional =
        _path(['RESAAS', 'User2', 'IsProfessional'], window) &&
        !_path(['RESAAS', 'User2', 'IsPendingProfessional'], window);
    const pendingRequests = [];
    const daysAgoCreated = GeneralUtility.daysAgo(
        GeneralUtility.getDateYMD(new Date()),
        _path(['RESAAS', 'User2', 'CreatedDate'], window)
    );
    const isPaid =
        _path(['RESAAS', 'User2', 'ReferralPro'], window) ||
        _path(['RESAAS', 'User2', 'PremiumPlus'], window);

    useEffect(() => {
        if (!isMe && imProfessional) {
            getConnections();
        }
        return () => {
            GeneralUtility.cleanupRequests(pendingRequests);
        };
    }, []);

    useEffect(() => {
        const c = connections.find(i => i.UserId === props.userId);

        if (c) {
            setConnection(c);
        }

        setTotalConnected(
            connections.reduce((a, c) => {
                if (c.Connected) {
                    return a + 1;
                }
                return a;
            }, 0)
        );
    }, [connections]);

    useEffect(() => {
        // CAN CONNECT IF: they are paid, accepting a connection, have < 5 connections or account is < 3 days old
        setCanConnect(isPaid || connection.FollowingMe || totalConnected < 5 || daysAgoCreated < 3);
    }, [totalConnected, connection]);

    const getConnections = () => {
        const connectionsBrowse = Connections.browse();
        GeneralUtility.appendPendingRequest(pendingRequests, connectionsBrowse);
        connectionsBrowse.request
            .then(response => {
                if (Array.isArray(response)) {
                    setConnections(response);
                }
            })
            .catch(error => {
                if (axios.isCancel(error)) {
                    return;
                }
                GeneralUtility.logWebRequestFailure(error, `fetch relationships from ConnectButton`);
            });
    };

    const connectButtonOnClick = () => {
        if (canConnect) {
            // accepting the request
            if ((!connection.Connected && connection.FollowingMe)) {
                handleConnectionRequest();
            } else {
                // sending a connect request
                setSendMessage(true);
            }
        } else {
            Mixpanel.add(MIXPANEL_LAND, { pageType: 'community paywall', type: 'connect button' });
        }
    }

    const handleConnectionRequest = (message) => {
        
        var successMessage;
        if (message) {
            setIsSubmitting(true);
        }
        
        Connections.add({
            contact: {
                KnownContactUserId: props.userId,
                InvitationMessage: message,
                Relationships: [
                    {
                        Type: 1,
                        Action: 0
                    }
                ]
            }
        })
            .then(results => {
                if (!_path(['data', 'SaveResult', 'Success'], results)) {
                    const errorMsg =
                        _path(['data', 'SaveResult', 'Message'], results) ===
                            resx(RESX_GLOBAL, 'RELATIONSHIP_ALREADY_EXISTS')
                            ? resx(RESX_GLOBAL, 'RELATIONSHIP_ALREADY_EXISTS')
                            : resx(RESX_GLOBAL, 'GENERIC_WEBSERVICE_ERROR_MESSAGE');

                    props.message.addMessage({ message: errorMsg, type: 'error' });
                } else {
                    if (!connection.Connected && connection.FollowingMe) {
                        successMessage = `You’re Connected! <a href="/inbox?UserId=${_path(['data', 'SaveResult', 'Data', 'KnownContactUserId'], results)}">Click here </a> to start a conversation and explore new opportunities together!`;
                    } else {
                        successMessage = 'Your connection request has been sent!';
                    }

                    props.message.addMessage({
                        message: successMessage,
                        type: 'success',
                        autoDelete: false,
                        perpetuate: false
                    });

                    getConnections();

                    Mixpanel.add(MIXPANEL_RELATIONSHIP, mixpanel());
                    if (message) {
                        Mixpanel.add(MIXPANEL_CONTENT, {
                            content: 'Send Message',
                            type: 'Connect request message'
                        });
                    }
                }
            })
            .catch(() => {
                props.message.addMessage({
                    message: resx(RESX_GLOBAL, 'GENERIC_WEBSERVICE_ERROR_MESSAGE'),
                    type: 'error'
                });
            })
            .then(() => {
                
                if (message) {
                    setIsSubmitting(false);
                }

                setSendMessage(false);
            });     
    };

    const buttonText = () => {
        let buttonText;
        if (connection.Connected) {
            buttonText = resx(RESX_GLOBAL, 'CONNECTION_BUTTON_CONNECTED');
        } else if (connection.ImFollowing) {
            buttonText = resx(RESX_GLOBAL, 'CONNECTION_BUTTON_REQUEST_SENT');
        } else if (connection.FollowingMe) {
            buttonText = resx(RESX_GLOBAL, 'CONNECTION_BUTTON_ACCEPT_REQUEST');
        } else {
            buttonText = resx(RESX_GLOBAL, 'CONNECTION_BUTTON_CONNECT');
        }
        return buttonText;
    };

    const className = () => {
        let className;

        if (connection.Connected) {
            className = `re-btn ${props.small ? 're-btn-small' : ''} re-btn-disabled re-btn-full-width re-icon re-icon-md re-icon-green re-icon-check-circle`;
        } else if (connection.ImFollowing) {
            className = `re-btn ${props.small ? 're-btn-small' : ''} re-btn-disabled re-btn-full-width re-icon re-icon-md re-icon-green re-icon-check-circle`;
        } else if (connection.FollowingMe) {
            className = `re-btn ${props.small ? 're-btn-small' : ''} re-btn-full-width`;
        } else {
            className = `re-btn ${props.small ? 're-btn-small' : ''} ${props.black ? 're-text-black' : ''} re-btn-subtle re-btn-full-width ${
                !canConnect ? 'ub-joinCommunityPaywall' : ''
            }`;
        }

        return className;
    };

    const mixpanel = () => {
        let mixpanel;

        if (!connection.ImFollowing && connection.FollowingMe) {
            mixpanel = {
                type: 'connection accepted',
                requester: `${RESAAS.User2.FirstName} ${RESAAS.User2.LastName}`,
                receiver: props.displayName,
                content: 'relationship accepted',
                element: props.mixpanelElement
            };
        } else if (!connection.ImFollowing && !connection.FollowingMe) {
            mixpanel = {
                type: 'connection request',
                requester: `${RESAAS.User2.FirstName} ${RESAAS.User2.LastName}`,
                receiver: props.displayName,
                element: props.mixpanelElement
            };
        }

        return mixpanel;
    };

    return (
        <React.Fragment>
            {!isMe && imProfessional ? (
                <div>
                    {sendMessage ? (
                        <SendMessage
                            userId={props.userId}
                            userFullName={props.displayName}
                            closeForm={() => setSendMessage(false)}
                            handleConnectionRequest={(message) => handleConnectionRequest(message)}
                            isSubmitting={isSubmitting}
                        />
                    ) : null}
                    <button className={className()} onClick={connectButtonOnClick} type="button" data-testid="connectbtn" data-mixpanel-name="Intent" data-mixpanel-type="Connect to an agent" data-mixpanel-context={props.mixpanelContext}>
                        {buttonText()}
                    </button>
                </div>
            ) : null}
        </React.Fragment>
    );
};

ConnectButton.propTypes = {
    userId: PropTypes.string.isRequired,
    message: PropTypes.object,
    displayName: PropTypes.string,
    mixpanelElement: PropTypes.string,
    mixpanelContext: PropTypes.string,
    small: PropTypes.bool
};

ConnectButton.defaultProps = {
    small: true,
    mixpanelElement: 'connect button'
};

export default withMessage(ConnectButton);
